// ═══════════════════════════════════════════════════════════════════════════════
// Reset password from email link (public)
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { resetPasswordInputSchema } from '@fdh/shared';
import { useLanguage } from '../../contexts/LanguageContext';
import { api } from '../../lib/api';
import { AuthLayout } from './AuthLayout';
import { PasswordField } from '../../components/auth/PasswordField';
import { AppButton } from '../../components/ui/AppButton';

export const ResetPasswordPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError('');

      if (!token) {
        setError(t('auth.missingToken'));
        return;
      }

      const parsed = resetPasswordInputSchema.safeParse({
        token,
        newPassword: password,
        confirmNewPassword: confirm,
      });
      if (!parsed.success) {
        const msg = parsed.error.issues.map((i) => i.message).join(', ');
        setError(msg);
        return;
      }

      setIsSubmitting(true);
      try {
        const response = await api.post<{ message?: string }>('/auth/reset-password', {
          token: parsed.data.token,
          newPassword: parsed.data.newPassword,
          confirmNewPassword: parsed.data.confirmNewPassword,
        });
        if (!response.success) {
          throw new Error(response.error ?? t('auth.errorGeneric'));
        }
        setSuccess(true);
      } catch (err) {
        setError(err instanceof Error ? err.message : t('auth.errorGeneric'));
      } finally {
        setIsSubmitting(false);
      }
    },
    [token, password, confirm, t]
  );

  const handleGoLogin = useCallback(() => {
    navigate('/login', { replace: true });
  }, [navigate]);

  return (
    <AuthLayout
      footer={
        <Link className="font-medium text-primary-600 underline" to="/login">
          {t('auth.linkLogin')}
        </Link>
      }
    >
      <h1 className="text-2xl font-bold text-gray-900" id="reset-heading">
        {t('auth.resetTitle')}
      </h1>
      <p className="mt-1 text-sm text-gray-500">{t('auth.resetSubtitle')}</p>

      {success ? (
        <div className="mt-6 space-y-4">
          <p className="rounded-lg bg-green-50 px-3 py-3 text-sm text-green-800" role="status">
            {t('auth.resetSuccess')}
          </p>
          <AppButton type="button" variant="primary" fullWidth onClick={handleGoLogin} className="shadow-sm">
            {t('auth.goToLogin')}
          </AppButton>
        </div>
      ) : (
        <form
          className="mt-6 space-y-4"
          onSubmit={handleSubmit}
          aria-labelledby="reset-heading"
          noValidate
        >
          {error ? (
            <div
              id="reset-error"
              className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700"
              role="alert"
            >
              {error}
            </div>
          ) : null}

          {!token ? (
            <p className="text-sm text-red-600" role="alert">
              {t('auth.missingToken')}
            </p>
          ) : null}

          <PasswordField
            id="reset-password"
            name="password"
            label={t('auth.newPasswordLabel')}
            value={password}
            onChange={setPassword}
            autoComplete="new-password"
            disabled={isSubmitting || !token}
            showLabels={{ show: t('auth.showPassword'), hide: t('auth.hidePassword') }}
            aria-describedby="reset-password-hint"
            aria-invalid={Boolean(error)}
          />
          <p id="reset-password-hint" className="mt-1 text-xs text-gray-500">
            {t('auth.passwordRulesHint')}
          </p>

          <PasswordField
            id="reset-confirm"
            name="confirmPassword"
            label={t('auth.confirmPasswordLabel')}
            value={confirm}
            onChange={setConfirm}
            autoComplete="new-password"
            disabled={isSubmitting || !token}
            showLabels={{ show: t('auth.showPassword'), hide: t('auth.hidePassword') }}
            aria-invalid={Boolean(error)}
          />

          <AppButton
            type="submit"
            variant="primary"
            fullWidth
            disabled={isSubmitting || !token}
            className="shadow-sm"
          >
            {isSubmitting ? t('auth.resetting') : t('auth.resetSubmit')}
          </AppButton>
        </form>
      )}
    </AuthLayout>
  );
};
